import React, { useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import clsx from 'clsx';
import type { AnyOfFilter, FilterOption } from '@app-types';
import { useDismissable } from '@hooks/useDismissable';
import field from './filterField.module.css';
import styles from './SelectFilter.module.css';

export interface SelectFilterProps {
  columnLabel: string;
  options: FilterOption[];
  value?: AnyOfFilter;
  onChange: (value: AnyOfFilter | null) => void;
}

/**
 * Multi-value selection filter for a column drawn from a known set.
 *
 * Selecting commits immediately — there is nothing to hold back the way the
 * range filters hold a half-typed bound. Values match exactly on the backend,
 * so picking "Active" cannot also return "Inactive" the way the old substring
 * filter did.
 */
const SelectFilter: React.FC<SelectFilterProps> = ({
  columnLabel,
  options,
  value,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = value?.values ?? [];

  useDismissable(containerRef, isOpen, () => setIsOpen(false));

  const toggleValue = (optionValue: string) => {
    const next = selected.includes(optionValue)
      ? selected.filter((entry) => entry !== optionValue)
      : [...selected, optionValue];

    onChange(next.length === 0 ? null : { kind: 'anyOf', values: next });
  };

  const clear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
    setIsOpen(false);
  };

  // A trigger carries its current state, not an instruction: "Any status" says
  // the column is unconstrained, where a button reading "Filter..." implied that
  // pressing it would filter something. The text inputs in the same row keep
  // "Filter..." — there the words are a placeholder, which is what they mean.
  const summary = () => {
    if (selected.length === 0) return `Any ${columnLabel.toLowerCase()}`;
    if (selected.length === 1) {
      return options.find((option) => option.value === selected[0])?.label ?? selected[0];
    }
    return `${selected.length} selected`;
  };

  const hasValue = selected.length > 0;

  return (
    <div className={styles.selectFilter} ref={containerRef}>
      <button
        type="button"
        className={clsx(field.field, styles.trigger, hasValue && field.hasValue)}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Filter by ${columnLabel}`}
      >
        <span className={clsx(field.summary, !hasValue && field.placeholder)}>
          {summary()}
        </span>
        {hasValue ? (
          <span
            className={field.affordance}
            onClick={clear}
            role="button"
            tabIndex={-1}
            aria-label={`Clear ${columnLabel} filter`}
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown size={14} className={styles.chevron} aria-hidden="true" />
        )}
      </button>

      {isOpen && (
        <div
          className={clsx(field.panel, styles.dropdown)}
          role="listbox"
          aria-multiselectable="true"
        >
          {/* An option set can legitimately be empty (e.g. Contract Type with no
              data). Saying so beats rendering a control that looks broken. */}
          {options.length === 0 ? (
            <div className={styles.emptyState}>No values available to filter by</div>
          ) : (
            options.map((option) => (
              <label key={option.value} className={styles.option}>
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => toggleValue(option.value)}
                />
                <span className={styles.optionLabel}>{option.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SelectFilter;
